const STORAGE_KEY = "wasaa.createMarket.autosave";
const SCHEMA_VERSION = 1;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function storage() {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
}

export function saveDraft(form, step, now = Date.now()) {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: SCHEMA_VERSION, savedAt: now, step, form }),
    );
    return true;
  } catch (e) {
    return false;
  }
}

export function loadDraft(now = Date.now()) {
  const store = storage();
  if (!store) return null;

  let raw;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    clearDraft();
    return null;
  }

  if (!parsed || parsed.version !== SCHEMA_VERSION || !parsed.form) {
    clearDraft();
    return null;
  }
  if (typeof parsed.savedAt !== "number" || now - parsed.savedAt > MAX_AGE_MS) {
    clearDraft();
    return null;
  }
  return { form: parsed.form, step: typeof parsed.step === "number" ? parsed.step : 0, savedAt: parsed.savedAt };
}

export function clearDraft() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nothing to do */
  }
}

export function isWorthSaving(form) {
  if (!form) return false;
  const text = [form.question, form.description, form.resolutionCriteria]
    .filter((v) => typeof v === "string")
    .join("")
    .trim();
  const hasPlacement =
    (Array.isArray(form.clusterIds) && form.clusterIds.length > 0) ||
    (Array.isArray(form.tagIds) && form.tagIds.length > 0) ||
    Boolean(form.county);
  return text.length > 0 || hasPlacement;
}

export function describeAge(savedAt, now = Date.now()) {
  const seconds = Math.max(0, Math.round((now - savedAt) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * The server half of the same autosave.
 *
 * localStorage above is the instant, offline-tolerant copy — it survives a refresh and
 * costs nothing. This is the durable one: it puts the half-written market in the
 * markets table as a DRAFT, so it is visible in the list, editable from any device, and
 * still there when the browser's site data is not.
 *
 * Returns the market id so subsequent saves update the same row rather than creating a
 * new market per keystroke. A failure is swallowed on purpose: autosave is a background
 * convenience and must never interrupt somebody mid-sentence. The local copy is still
 * written either way, so nothing is lost when this is what failed.
 */
export async function saveServerDraft(apiPost, form, marketId) {
  const body = { question: form.question || undefined };
  if (marketId) body.marketId = marketId;
  if (form.slug) body.slug = form.slug;
  if (form.description) body.description = form.description;
  if (form.closesAt) body.closesAt = form.closesAt;
  if (form.settlementDeadlineAt) body.settlementDeadlineAt = form.settlementDeadlineAt;
  if (form.resolutionCriteria) {
    body.settlementRule = { resolutionCriteria: form.resolutionCriteria };
  }

  try {
    const result = await apiPost("/portal/markets/draft", body);
    return result?.market?.id ?? marketId ?? null;
  } catch (e) {
    return marketId ?? null;
  }
}
